// src/components/DynamicForm.js

import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Grid,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  Button as MuiButton,
  Autocomplete,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { Controller, useWatch, useFormContext } from 'react-hook-form';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatPrice, calculateDiscountedPrice } from '../utils/priceUtils';
import CustomDatePicker from '../components/CustomDatePicker';
import { getLookupValues, createLookupValue } from '../services/lookupService';
import { OrgContext } from '../contexts/OrgContext';

const DynamicForm = ({
  schema,
  control,
  errors,
  disabledFields = {},
  requiredFields = {},
  optionsData = {},
  customerOptions = [],
  inventoryOptions = [],
  selectedCustomer,
  setSelectedCustomer,
  supplierOptions = [],
  priceLabels,
}) => {
  const { orgId } = useContext(OrgContext);
  const [taxPercentageOptions, setTaxPercentageOptions] = useState([]);
  const [loadingTaxOptions, setLoadingTaxOptions] = useState(false);
  const watchedFields = useWatch({ control });
  const { setValue } = useFormContext();
  
  // Remove the automatic clearing of product line items on order type change
  // This was causing the form to lose data on validation errors
  // React.useEffect(() => {
  //   if (watchedFields.order_type) {
  //     setValue('product_line_items', [], { shouldValidate: true });
  //   }
  // }, [watchedFields.order_type, setValue]);

  // Memoize fetchTaxPercentages to avoid dependency issues
  const fetchTaxPercentages = useCallback(async () => {
    setLoadingTaxOptions(true);
    try {
      const taxOptions = await getLookupValues(orgId, 'tax_percentage');
      setTaxPercentageOptions(taxOptions);
    } catch (error) {
      console.error('Failed to fetch tax percentages:', error);
    } finally {
      setLoadingTaxOptions(false);
    }
  }, [orgId]);

  // Load tax percentage options when component mounts
  useEffect(() => {
    if (orgId) {
      fetchTaxPercentages();
    }
  }, [orgId, fetchTaxPercentages]);

  const ProductLineItemsInput = ({
    value = [],
    onChange,
    inventoryOptions,
    error,
    helperText,
    control,
    priceLabels
  }) => {
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [quantity, setQuantity] = useState('');
  const [barcodeBuffer, setBarcodeBuffer] = useState('');
  const [lastKeyTime, setLastKeyTime] = useState(0);
  const [scanFeedback, setScanFeedback] = useState({ open: false, message: '', severity: 'error' });
    const watchedFields = useWatch({ control }); // Now control is properly passed
    const isPurchaseOrder = watchedFields.order_type === 'Purchase';

    // Function to handle barcode scan completion
    const handleBarcodeScan = useCallback((rawCode) => {
      if (!rawCode) return;
      const scannedCode = rawCode.trim();
      const upper = scannedCode.toUpperCase();
      // Minimal change: allow match on scannerReference (case-insensitive) OR sku (case-insensitive)
      const foundProduct = inventoryOptions.find(product => {
        const refs = [product.scannerReference, product.sku]
          .filter(Boolean)
          .map(v => v.toString().trim().toUpperCase());
        return refs.includes(upper);
      });

      if (foundProduct) {
        // Check if product already exists in line items
        const existingItemIndex = value.findIndex(
          (item) => item.product_id === foundProduct.value
        );

        if (existingItemIndex >= 0) {
          // Product exists, increase quantity by 1
          const updatedItems = [...value];
          const newQuantity = updatedItems[existingItemIndex].quantity + 1;
          const unitPrice = parseFloat(updatedItems[existingItemIndex].unit_price);
          updatedItems[existingItemIndex] = {
            ...updatedItems[existingItemIndex],
            quantity: newQuantity,
            total_price: newQuantity * unitPrice
          };
          onChange(updatedItems);
        } else {
          // Product doesn't exist, add it with quantity 1
          const unitPrice = isPurchaseOrder 
            ? parseFloat(foundProduct.unit_cost) 
            : parseFloat(foundProduct.unit_price);
          const taxMeta = foundProduct?.meta?.tax;
          const autoTax = Number(taxMeta?.final_percentage) || null;
          const newItem = {
            product_id: foundProduct.value,
            product_name: foundProduct.label,
            sku: foundProduct.sku,
            unit_price: unitPrice,
            original_unit_price: unitPrice,
            quantity: 1,
            total_price: unitPrice * 1,
            discount_type: 'none',
            discount_value: 0,
            tax_meta: taxMeta || null,
          };
          onChange([...value, newItem]);
          if (autoTax !== null && (watchedFields.tax_percentage === null || watchedFields.tax_percentage === undefined || watchedFields.tax_percentage === '')) {
            setValue('tax_percentage', autoTax, { shouldValidate: true, shouldDirty: true });
          }
        }
        
        // Show a temporary success message
        console.log("Product scanned:", scannedCode);
      } else {
        console.log("Product not found for scanned code:", scannedCode);
        setScanFeedback({ open: true, message: `Product not found: ${scannedCode}`, severity: 'error' });
      }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryOptions, isPurchaseOrder, value, onChange]);

    // Setup global keydown listener for barcode scanner
    useEffect(() => {
      // Reverted simpler logic with slightly increased timeout for mixed alphanumeric scans
      const SCANNER_TIMEOUT = 200; // ms between chars to consider same scan (was 100)

      const handleKeyDown = (e) => {
        const currentTime = Date.now();
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

        if (e.key === 'Enter') {
          if (barcodeBuffer) {
            console.debug('[Scanner] Enter detected. Final buffer =>', barcodeBuffer);
            handleBarcodeScan(barcodeBuffer);
            setBarcodeBuffer('');
          }
          return;
        }

        if (e.key.length === 1) {
          const delta = currentTime - lastKeyTime;
          const sameScan = (delta < SCANNER_TIMEOUT) || barcodeBuffer === '';
          const next = sameScan ? (barcodeBuffer + e.key) : e.key; // reset if gap too large
          console.debug(`[Scanner] key='${e.key}' delta=${delta}ms sameScan=${sameScan} buffer='${next}'`);
          setBarcodeBuffer(next);
          setLastKeyTime(currentTime);
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [barcodeBuffer, lastKeyTime, handleBarcodeScan]);

  const handleAddProduct = () => {
      if (selectedProduct && quantity > 0) {
        // Check if product already exists in line items
        const existingItemIndex = value.findIndex(
          (item) => item.product_id === selectedProduct.value
        );

        if (existingItemIndex >= 0) {
          // Product exists, update quantity and total price
          const updatedItems = [...value];
          const newQuantity = updatedItems[existingItemIndex].quantity + parseInt(quantity, 10);
          const unitPrice = parseFloat(updatedItems[existingItemIndex].unit_price);
          updatedItems[existingItemIndex] = {
            ...updatedItems[existingItemIndex],
            quantity: newQuantity,
            total_price: newQuantity * unitPrice
          };
          onChange(updatedItems);
        } else {
          // Product doesn't exist, add it with specified quantity
          const unitPrice = isPurchaseOrder 
            ? parseFloat(selectedProduct.unit_cost) 
            : parseFloat(selectedProduct.unit_price);
          const qty = parseInt(quantity, 10);
          
          const taxMeta = selectedProduct?.meta?.tax;
          const autoTax = Number(taxMeta?.final_percentage) || null;
          const newItem = {
            product_id: selectedProduct.value,
            product_name: selectedProduct.label,
            sku: selectedProduct.sku,
            unit_price: unitPrice,
            original_unit_price: unitPrice,
            quantity: qty,
            total_price: unitPrice * qty,
            discount_type: 'none',
            discount_value: 0,
            tax_meta: taxMeta || null,
          };

          onChange([...value, newItem]);
          // If order has a Tax Percentage field in parent form, auto-fill it only if empty
          if (autoTax !== null && (watchedFields.tax_percentage === null || watchedFields.tax_percentage === undefined || watchedFields.tax_percentage === '')) {
            setValue('tax_percentage', autoTax, { shouldValidate: true, shouldDirty: true });
          }
        }
        setSelectedProduct(null);
        setQuantity('');
      }
    };

    const handleRemoveProduct = (index) => {
      const updatedValue = [...value];
      updatedValue.splice(index, 1);
      onChange(updatedValue);
    };

    // Helper to update a line item at index
    const updateLineItem = (index, changes) => {
      const updated = [...value];
      updated[index] = { ...updated[index], ...changes };
      // Recalculate unit_price and total_price if discount fields change
      const originalPrice = updated[index].original_unit_price ?? updated[index].unit_price ?? 0;
      const quantity = updated[index].quantity ?? 1;
      const discountType = updated[index].discount_type || 'none';
      const discountValue = updated[index].discount_value || 0;
      const { unitPrice, totalPrice } = calculateDiscountedPrice(
        originalPrice,
        quantity,
        discountType,
        discountValue
      );
      updated[index].unit_price = unitPrice;
      updated[index].total_price = totalPrice;
      onChange(updated);
    };

    return (
      <Grid container spacing={2}>
        <Snackbar
          open={scanFeedback.open}
          autoHideDuration={3000}
          onClose={() => setScanFeedback(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            severity={scanFeedback.severity}
            variant="filled"
            onClose={() => setScanFeedback(prev => ({ ...prev, open: false }))}
            sx={{ width: '100%' }}
          >
            {scanFeedback.message}
          </Alert>
        </Snackbar>
        <Grid item xs={12}>
          <Typography variant="subtitle1">Add Products to Order</Typography>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
            Barcode scanning is active. Scan any product to add it to your order.
          </Typography>
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="subtitle2">Manually Add Products:</Typography>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={inventoryOptions}
            getOptionLabel={(option) => `${option.label} (SKU: ${option.sku})`}
            value={selectedProduct}
            onChange={(event, newValue) => setSelectedProduct(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Select Product" variant="outlined" autoComplete="off" />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Quantity"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            variant="outlined"
            fullWidth
            autoComplete="off"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Button variant="contained" color="primary" onClick={handleAddProduct} fullWidth>
            Add Product
          </Button>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Typography color="error">{helperText}</Typography>
          </Grid>
        )}

        {value.length > 0 && (
          <Grid item xs={12}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product Name</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Tax Info</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Original Price</TableCell>
                    <TableCell>Discount Type</TableCell>
                    <TableCell>Discount Value</TableCell>
                    <TableCell>Final Price</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {value.map((item, index) => {
                    const originalPrice = item.original_unit_price ?? item.unit_price ?? 0;
                    const quantity = item.quantity ?? 1;
                    const discountType = item.discount_type || 'none';
                    const discountValue = item.discount_value || 0;
                    const { unitPrice, totalPrice } = calculateDiscountedPrice(
                      originalPrice,
                      quantity,
                      discountType,
                      discountValue
                    );
                    return (
                      <TableRow key={index}>
                        <TableCell>{item.product_name}</TableCell>
                        <TableCell>{item.sku}</TableCell>
                        <TableCell>
                          {item.tax_meta ? (
                            <div style={{ maxWidth: 220 }}>
                              <div style={{ fontSize: 12 }}>Final: {item.tax_meta.final_percentage}%</div>
                              {item.tax_meta.gst?.type && (
                                <div style={{ fontSize: 11, color: '#666' }}>GST: {item.tax_meta.gst.type}{item.tax_meta.gst.conditionType ? ` (${item.tax_meta.gst.conditionType})` : ''}</div>
                              )}
                              {typeof item.tax_meta?.cess?.rates?.total === 'number' && (
                                <div style={{ fontSize: 11, color: '#666' }}>Cess: {item.tax_meta.cess.rates.total}%</div>
                              )}
                            </div>
                          ) : (
                            <span style={{ fontSize: 12, color: '#999' }}>—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={item.quantity}
                            onChange={e => updateLineItem(index, { quantity: parseInt(e.target.value, 10) || 1 })}
                            sx={{ width: 70 }}
                            autoComplete="off"
                          />
                        </TableCell>
                        <TableCell>{formatPrice(originalPrice)}</TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            value={discountType}
                            onChange={e => updateLineItem(index, { discount_type: e.target.value })}
                            sx={{ width: 100 }}
                          >
                            <MenuItem value="none">None</MenuItem>
                            <MenuItem value="percentage">%</MenuItem>
                            <MenuItem value="amount">Amount</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={discountValue}
                            onChange={e => updateLineItem(index, { discount_value: e.target.value })}
                            sx={{ width: 80 }}
                            disabled={discountType === 'none'}
                            autoComplete="off"
                          />
                        </TableCell>
                        <TableCell>{formatPrice(unitPrice)}</TableCell>
                        <TableCell>{formatPrice(totalPrice)}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            edge="end"
                            color="inherit"
                            onClick={() => handleRemoveProduct(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <Grid container spacing={2}>
      {schema.map((field) => {
        // Handle conditional rendering
        if (field.conditional) {
          const { field: dependencyField, value: dependencyValue } = field.conditional;
          if (watchedFields[dependencyField] !== dependencyValue) {
            return null;
          }
        }

        // Determine if the field should be disabled
        const isDisabled = field.disabled || disabledFields[field.name];
        // Determine if the field should be required
        const isRequired = field.required || requiredFields[field.name];

        switch (field.type) {
          case 'text':
          case 'number':
          case 'textarea':
          case 'email':
            return (
              <Grid item xs={12} sm={6} key={field.name}>
                <Controller
                  name={field.name}
                  control={control}
                  rules={{
                    required: isRequired,
                    ...(field.validation || {}),
                  }}
                  render={({ field: controllerField }) => (
                    <TextField
                      {...controllerField}
                      label={field.label}
                      type={field.type === 'textarea' ? 'text' : field.type}
                      fullWidth
                      required={isRequired}
                      placeholder={field.placeholder}
                      disabled={isDisabled}
                      multiline={field.type === 'textarea'}
                      minRows={field.type === 'textarea' ? 3 : undefined}
                      error={!!errors[field.name]}
                      helperText={
                        errors[field.name]
                          ? errors[field.name].type === 'required'
                            ? `${field.label} is required`
                            : errors[field.name].type === 'pattern'
                            ? errors[field.name].message || 'Invalid format'
                            : errors[field.name].message
                          : ''
                      }
                      InputLabelProps={{ shrink: true }}
                      autoComplete={
                        field.type === 'email' ? 'email' :
                        field.name?.toLowerCase().includes('phone') || 
                        field.name?.toLowerCase().includes('contact') || 
                        field.name?.toLowerCase().includes('whatsapp') ? 'tel' : 'off'
                      }
                    />
                  )}
                />
              </Grid>
            );

          case 'checkbox':
            return (
              <Grid item xs={12} sm={6} key={field.name}>
                <Controller
                  name={field.name}
                  control={control}
                  render={({ field: controllerField }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...controllerField}
                          checked={!!controllerField.value}
                          disabled={isDisabled}
                        />
                      }
                      label={field.label}
                    />
                  )}
                />
              </Grid>
            );

          case 'select':
            return (
              <Grid item xs={12} sm={6} key={field.name}>
                <FormControl
                  fullWidth
                  required={isRequired}
                  error={!!errors[field.name]}
                  disabled={isDisabled}
                >
                  <InputLabel>{field.label}</InputLabel>
                  <Controller
                    name={field.name}
                    control={control}
                    rules={{ required: isRequired }}
                    render={({ field: controllerField }) => (
                      <Select
                        {...controllerField}
                        label={field.label}
                        disabled={isDisabled}
                      >
                        {field.options.map((option) => (
                          <MenuItem value={option} key={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  <FormHelperText>
                    {errors[field.name] ? `${field.label} is required` : ''}
                  </FormHelperText>
                </FormControl>
              </Grid>
            );

          case 'multi-select-searchable':
            return (
              <Grid item xs={12} sm={6} key={field.name}>
                <Controller
                  name={field.name}
                  control={control}
                  rules={{ required: isRequired }}
                  render={({ field: controllerField }) => (
                    <Autocomplete
                      multiple
                      options={optionsData[field.name] || []}
                      getOptionLabel={(option) => option}
                      value={controllerField.value || []}
                      onChange={(event, value) => controllerField.onChange(value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={field.label}
                          placeholder={field.placeholder}
                          error={!!errors[field.name]}
                          helperText={
                            errors[field.name]
                              ? errors[field.name].type === 'required'
                                ? `${field.label} is required`
                                : errors[field.name].message
                              : ''
                          }
                          disabled={isDisabled}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
            );

          case 'file':
            return (
              <Grid item xs={12} sm={6} key={field.name}>
                <Controller
                  name={field.name}
                  control={control}
                  rules={{ required: isRequired }}
                  render={({ field: controllerField }) => (
                    <>
                      <MuiButton
                        variant="outlined"
                        component="label"
                        fullWidth
                        disabled={isDisabled}
                      >
                        {field.label}
                        <input
                          type="file"
                          hidden
                          accept={field.accept || '*'}
                          onChange={(e) => {
                            controllerField.onChange(e.target.files[0]);
                          }}
                        />
                      </MuiButton>
                      {errors[field.name] && (
                        <FormHelperText error>
                          {`${field.label} is required`}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </Grid>
            );

          case 'product-line-items':
            return (
              <Grid item xs={12} key={field.name}>
                <Controller
                  name={field.name}
                  control={control}
                  rules={{
                    required: isRequired,
                    validate: value => (Array.isArray(value) && value.length > 0) || `${field.label} is required`
                  }}
                  render={({ field: controllerField }) => (
                    <ProductLineItemsInput
                      value={controllerField.value}
                      onChange={controllerField.onChange}
                      error={!!errors[field.name]}
                      helperText={
                        errors[field.name]
                          ? errors[field.name].type === 'required' || errors[field.name].type === 'validate'
                            ? `${field.label} is required`
                            : errors[field.name].message
                          : ''
                      }
                      disabled={isDisabled}
                      inventoryOptions={inventoryOptions}
                      priceLabels={priceLabels}
                      control={control}
                    />
                  )}
                />
              </Grid>
            );
          case 'customer-select':
            return (
                <Grid item xs={12} sm={6} key={field.name}>
                <Controller
                    name={field.name}
                    control={control}
                    rules={{ 
                      required: isRequired || (watchedFields.order_type !== 'Purchase' && watchedFields.order_type !== undefined),
                      validate: value => {
                        if (watchedFields.order_type !== 'Purchase' && watchedFields.order_type !== undefined && !value) {
                          return 'Customer is required for non-purchase orders';
                        }
                        return true;
                      }
                    }}
                    render={({ field: controllerField }) => (
                    <Autocomplete
                        options={customerOptions}
                        getOptionLabel={(option) =>
                        `${option.customer_name} (ID: ${option.id}, Phone: ${option.contact_number})`
                        }
                        value={
                        customerOptions.find(
                            (option) => option.id === controllerField.value
                        ) || null
                        }
                        onChange={(event, newValue) => {
                            controllerField.onChange(newValue ? newValue.id : '');
                            setSelectedCustomer(newValue);
                        }}
                        renderInput={(params) => (
                        <TextField
                            {...params}
                            label={field.label}
                            placeholder={field.placeholder}
                            error={!!errors[field.name]}
                            helperText={
                            errors[field.name]
                                ? errors[field.name].type === 'required'
                                ? `${field.label} is required`
                                : errors[field.name].message
                                : ''
                            }
                            InputLabelProps={{ shrink: true }}
                            required={isRequired || (watchedFields.order_type !== 'Purchase' && watchedFields.order_type !== undefined)}
                            autoComplete="off"
                        />
                        )}
                        disabled={isDisabled}
                    />
                    )}
                />
                </Grid>
            );
          case 'supplier-select':
            return (
              <Grid item xs={12} sm={6} key={field.name}>
                <Controller
                  name={field.name}
                  control={control}
                  rules={{ 
                    required: isRequired || (watchedFields.order_type === 'Purchase' && watchedFields.order_type !== undefined),
                    validate: value => {
                      if (watchedFields.order_type === 'Purchase' && watchedFields.order_type !== undefined && !value) {
                        return 'Supplier is required for purchase orders';
                      }
                      return true;
                    }
                  }}
                  render={({ field: controllerField }) => (
                    <Autocomplete
                      options={supplierOptions}
                      getOptionLabel={(option) =>
                        `${option.supplier_name} (ID: ${option.id}, Phone: ${option.contact_number})`
                      }
                      value={
                        supplierOptions.find(
                          (option) => option.id === controllerField.value
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        controllerField.onChange(newValue ? newValue.id : '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={field.label}
                          placeholder={field.placeholder}
                          error={!!errors[field.name]}
                          helperText={
                            errors[field.name]
                              ? errors[field.name].type === 'required'
                                ? `${field.label} is required`
                                : errors[field.name].message
                              : ''
                          }
                          InputLabelProps={{ shrink: true }}
                          required={isRequired || (watchedFields.order_type === 'Purchase' && watchedFields.order_type !== undefined)}
                          autoComplete="off"
                        />
                      )}
                      disabled={isDisabled}
                    />
                  )}
                />
              </Grid>
            );
            case 'date':
              return (
                <Grid item xs={12} sm={6} key={field.name}>
                  <Controller
                    name={field.name}
                    control={control}
                    rules={{
                      required: isRequired,
                      ...(field.validation || {}),
                    }}
                    render={({ field: controllerField }) => (
                      <CustomDatePicker
                        {...controllerField}
                        label={field.label}
                        value={controllerField.value}
                        onChange={controllerField.onChange}
                        required={isRequired}
                        disabled={isDisabled}
                        sx={{ width: '100%' }}
                        error={!!errors[field.name]}
                        helperText={
                          errors[field.name]
                            ? errors[field.name].type === 'required'
                              ? `${field.label} is required`
                              : errors[field.name].message
                            : ''
                        }
                      />
                    )}
                  />
                </Grid>
              );
          case 'tax-percentage-lookup':
            return (
              <Grid item xs={12} sm={6} key={field.name}>
                <Controller
                  name={field.name}
                  control={control}
                  rules={{
                    required: isRequired,
                    validate: value => {
                      // Allow empty, null, undefined values - treat as 0
                      if (value === '' || value === null || value === undefined) {
                        return true;
                      }
                      const numValue = Number(value);
                      return isNaN(numValue) || numValue < 0 || numValue > 100 
                        ? 'Tax percentage must be between 0 and 100' 
                        : true;
                    }
                  }}
                  render={({ field: controllerField, fieldState: { error } }) => (
                    <Autocomplete
                      value={controllerField.value?.toString() || ''}
                      onChange={async (event, newValue) => {
                        // Handle empty values
                        if (newValue === '' || newValue === null || newValue === undefined) {
                          controllerField.onChange(null);
                          return;
                        }
                        
                        // Convert to number if it's a valid number
                        const numValue = Number(newValue);
                        controllerField.onChange(isNaN(numValue) ? newValue : numValue);
                        
                        // Save new tax percentage value to lookup table if it doesn't exist
                        if (newValue && newValue.toString().trim() !== '' && !taxPercentageOptions.includes(newValue.toString())) {
                          try {
                            console.log('Saving new tax percentage to lookup table:', newValue);
                            await createLookupValue(orgId, 'tax_percentage', newValue.toString());
                            console.log('Successfully saved tax percentage');
                            
                            // Update local options
                            setTaxPercentageOptions(prev => [...prev, newValue.toString()]);
                          } catch (error) {
                            console.error('Failed to save new tax percentage:', error);
                            // Don't throw error - allow form to continue working
                          }
                        }
                      }}
                      inputValue={controllerField.value?.toString() || ''}
                      onInputChange={(event, newInputValue) => {
                        // Allow empty input
                        if (newInputValue === '') {
                          controllerField.onChange(null);
                          return;
                        }
                        
                        // Allow direct typing but validate on selection
                        const numValue = Number(newInputValue);
                        if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                          controllerField.onChange(numValue);
                        }
                      }}
                      options={taxPercentageOptions.map(option => option.toString())}
                      freeSolo
                      fullWidth
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={field.label}
                          placeholder={field.placeholder || "Enter tax percentage (leave blank for 0%)"}
                          error={!!error}
                          helperText={error ? error.message : 'Leave blank for 0% tax'}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingTaxOptions ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      disabled={isDisabled}
                    />
                  )}
                />
              </Grid>
            );
          
          default:
            return null;
        }
      })}
    </Grid>
  );
};

export default DynamicForm;
